<template>
    <article
        :id="thumbnail ? undefined : screen && screen.id"
        class="screen"
        :class="[capsule.is, type, { placeholder, preview, editable, touch: $browser.is.touch }]"
        :style="{
            backgroundColor,
            color: textColour,
            '--bleed': thumbnail ? '64px' : capsuleInnerWidth > 900 ? `${(capsuleInnerWidth - 900) / 2}px` : null,
            '--pattern-colour': patternColour,
            '--background-colour': backgroundColor,
            '--button-colour': buttonColour,
            '--button-colour-contrast': buttonColourContrast,
            '--title-font': headingFont,
            '--heading': headingFont,
            '--title-colour': titleColour,
            '--placeholder-colour': placeholderColour,
            '--colour-rgb': colourRgb,
            '--colour-contrast-rgb': colourContrastRgb,
            '--focus-colour': textColour
        }"
    >
        <Pattern
            v-if="type === 'finish' && screen.object.display_pattern !== false && course.theme"
            :type="course.theme.preset"
        />
        <Photo
            v-if="hasBackgroundImage"
            :src="url(localScreen.asset)"
            :alt="localScreen.title"
            no-background
            cover
            @loaded="$emit('image:loaded')"
        />
        <span
            v-if="hasBackgroundImage && localScreen.object.overlay !== null"
            class="background-overlay"
            :style="{ opacity: localScreen.object.overlay }"
        />
        <ScrollArea
            v-if="!background"
            ref="scroll"
            hide-bar
            class="screen-inner"
            content-class="screen-content"
            :scroll-element="scrollEl"
            @scroll:down="$emit('scroll:down')"
            @scroll:up="$emit('scroll:up')"
            @scroll:top="$emit('scroll:top')"
            @scroll:bottom="$emit('scroll:bottom')"
            @vue:mounted="$timeout(() => isScrollMounted = true, 100)"
        >
            <Transition
                :name="(thumbnail || placeholder || _editable || capsule.is.mobile) ? null : 'fadeveryslow'"
                mode="out-in"
            >
                <span
                    v-if="screenComponent"
                    v-show="thumbnail || isScrollMounted"
                    class="screen-component-wrapper"
                >
                    <Component
                        :is="screenComponent"
                        ref="screen"
                        :key="screen.id"
                        :tile="localScreen"
                        :course="course"
                        :simple="simple"
                        :colour="colourClass"
                        :thumbnail="thumbnail"
                        :preview="preview"
                        :visible="isVisible"
                        :selected-item="selectedItem"
                        :scroll-element="scrollEl"
                        class="screen-component"
                        @saved="onSave"
                        @navigate="$emit('navigate', $event)"
                        @next="$emit('next', $event)"
                        @complete="setTileComplete"
                        @login="$emit('login', $event)"
                        @upgrade="$emit('upgrade', $event)"
                        @update="onScreenUpdate"
                        @save="onScreenUpdate"
                        @context="$emit('context', screen.id, $event)"
                        @update:tile="onScreenUpdate"
                        @quiz:answer="$emit('quiz:answer', $event)"
                        @quiz:retry="$emit('quiz:retry')"
                        @image:loaded="$emit('image:loaded')"
                        @feedback="$emit('feedback', $event)"
                    />
                </span>
            </Transition>
        </ScrollArea>
        <Generate
            v-if="isGenerating"
            ref="generate"
            :screen="screen"
            :course="course"
            @submit="suggestScreen"
            @close="isGenerating = false"
        />
        <div v-if="isGeneratingScreen" class="float-nav screen-generate">
            <div class="toolbar generate">
                <template v-if="localScreen.generating">
                    <Animation src="generating" width="24" height="24" />
                    <Txt class="generating">
                        Generating...
                    </Txt>
                    <hr />
                    <Button
                        variant="create"
                        colour="white"
                        size="xxxs" heading
                        slim
                        @click="cancelGenerating"
                    >
                        Cancel
                    </Button>
                </template>
                <template v-else-if="isGenerateError">
                    <Icon name="danger" class="text-danger" />
                    <Txt>
                        Uh-oh! Something went wrong
                    </Txt>
                    <Button
                        icon="reverse"
                        icon-size="20"
                        icon-pos="left"
                        variant="create"
                        colour="white"
                        size="xxxs" heading
                        slim
                        @click.stop="cancelGenerating"
                    >
                        Try again
                    </Button>
                    <hr />
                    <Button
                        icon="correct"
                        icon-size="20"
                        icon-pos="right"
                        variant="create"
                        colour="white"
                        size="xxxs" heading
                        slim
                        @click.stop="cancelGenerating"
                    >
                        Cancel
                    </Button>
                </template>
                <template v-else>
                    <Icon name="correct-circle" class="text-success" />
                    <Txt>
                        Done!
                    </Txt>
                    <Button
                        icon="reverse"
                        icon-size="20"
                        icon-pos="left"
                        variant="create"
                        colour="white"
                        size="xxxs" heading
                        slim
                        @click.stop="cancelGenerating"
                    >
                        Try again
                    </Button>
                    <hr />
                    <Button
                        icon="correct"
                        icon-size="20"
                        icon-pos="right"
                        variant="create"
                        colour="white"
                        size="xxxs" heading
                        slim
                        @click.stop="confirmGenerating"
                    >
                        Keep
                    </Button>
                </template>
            </div>
        </div>
    </article>
</template>

<script>
import { computed } from 'vue';
import { url, copy } from '@/helpers/utilities';
import { defineAsyncComponent } from 'vue';
import { isCompletable } from '@/helpers/utilities';
import { useResponsive } from '@/store/responsive';
import { readable, hexToRgb } from '@/helpers/colour';
import { COLOUR_DARK, COLOUR_LIGHT } from '@/store/theme';
import { getItemKeyByType, getImageKeyByType } from '@/helpers/tile';

import useBuilderState from '@/store/builder.author';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Photo from '@/components/media/photo';
import Button from '@/components/utility/button';
import Observer from '@/components/utility/observer';
import Animation from '@/components/animation/animation';
import ScrollArea from '@/components/utility/scroll-area';
import Generate from '@/components/author/modals/generate';

const Pattern = defineAsyncComponent(() => import('@/components/author/course/pattern'));
const Toolbar = defineAsyncComponent(() => import('@/components/author/course/toolbar'));

const DocumentEditor = defineAsyncComponent(() => import('@/pages/builder/author/document'));
const EmbedEditor = defineAsyncComponent(() => import('@/pages/builder/author/embed'));
const AccordionEditor = defineAsyncComponent(() => import('@/pages/builder/author/accordion'));
const ChecklistEditor = defineAsyncComponent(() => import('@/pages/builder/author/checklist'));
const CoverEditor = defineAsyncComponent(() => import('@/pages/builder/author/cover'));
const FlashCardEditor = defineAsyncComponent(() => import('@/pages/builder/author/flash-cards'));
const HotspotEditor = defineAsyncComponent(() => import('@/pages/builder/author/hotspot'));
const ProcessEditor = defineAsyncComponent(() => import('@/pages/builder/author/process'));
const QuizEditor = defineAsyncComponent(() => import('@/pages/builder/author/quiz'));
const SlideshowEditor = defineAsyncComponent(() => import('@/pages/builder/author/slideshow'));
const TextAndImageEditor = defineAsyncComponent(() => import('@/pages/builder/author/text-image'));
const VideoEditor = defineAsyncComponent(() => import('@/pages/builder/author/video'));

const Document = defineAsyncComponent(() => import( '@/pages/student/author/document'));
const Embed = defineAsyncComponent(() => import('@/pages/student/author/embed'));
const Accordion = defineAsyncComponent(() => import('@/pages/student/author/accordion'));
const Checklist = defineAsyncComponent(() => import('@/pages/student/author/checklist'));
const Cover = defineAsyncComponent(() => import('@/pages/student/author/cover'));
const FlashCard = defineAsyncComponent(() => import('@/pages/student/author/flash-cards'));
const Hotspot = defineAsyncComponent(() => import('@/pages/student/author/hotspot'));
const Process = defineAsyncComponent(() => import('@/pages/student/author/process'));
const Quiz = defineAsyncComponent(() => import('@/pages/student/author/quiz'));
const Slideshow = defineAsyncComponent(() => import('@/pages/student/author/slideshow'));
const TextAndImage = defineAsyncComponent(() => import('@/pages/student/author/text-image'));
const Video = defineAsyncComponent(() => import('@/pages/student/author/video'));

export default {
    name: 'Screen',
    components: { Pattern, Toolbar, Photo, Observer, ScrollArea, Generate, Icon, Button, Txt, Animation },
    provide() {
        return {
            target: computed(() => this.el),
        };
    },
    inject: ['$global'],
    props: {
        screen: { type: Object, default: null },
        course: { type: Object, default: null },
        selected: { type: Number, default: null },
        selectedItem: { type: Number, default: 0 },

        placeholder: { type: Boolean, default: false },
        preview: { type: Boolean, default: false },
        simple: { type: Boolean, default: false },
        editable: { type: Boolean, default: false },
        thumbnail: { type: Boolean, default: false },
        background: { type: Boolean, default: false }
    },
    emits: [
        'update:screen', 'saved', 'login', 'upgrade', 'context', 'progress', 'next', 'navigate', 'feedback',
        'screen:start', 'screen:end', 'screen:complete',
        'quiz:answer', 'quiz:retry', 'image:loaded',
        'scroll:down', 'scroll:up', 'scroll:bottom', 'scroll:top'
    ],
    data() {
        return {
            localScreen: copy(this.screen),
            isScrollMounted: false,
            scrollEl: null,
            startTime: null,

            el: null,
            capsule: useResponsive('capsule'),
            isGenerating: false,
            isGenerateError: false,
            isGeneratingScreen: false
        };
    },
    computed: {
        capsuleInnerWidth() {
            return this.capsule.width - (this.screenPadding * 2);
        },
        screenPadding() {
            if (this.capsule.is.xs) return 24;
            if (this.capsule.is.sm) return 24;
            if (this.capsule.is.md) return 48;
            if (this.capsule.is.lg) return 72;
            if (this.capsule.is.xl) return 96;
            return 24;
        },
        type() {
            return this.screen
                && this.screen.type
                && this.screen.type.name;
        },
        isVisible() {
            if (this.thumbnail) return true;
            if (this.editable) return false;
            return this.screen.sequence === this.selected;
        },
        isCloseToSelected() {
            return !this.selected
                || this.screen.sequence === this.selected
                || this.screen.sequence === this.selected - 1
                || this.screen.sequence === this.selected + 1;
        },
        hasDefaultBackground() {
            return ['iaAcronym', 'iaChecklist', 'caseStudy', 'process', 'quiz'].includes(this.type);
        },
        hasBackgroundImage() {
            return this.localScreen
                && this.localScreen.asset
                && this.localScreen.object
                && this.localScreen.object.layout === 'background';
        },
        hasBackgroundColour() {
            return this.localScreen
                && this.localScreen.object
                && this.localScreen.object.bg_colour;
        },
        hasTheme() {
            return this.course
                && this.course.theme
                && this.course.theme.colours
                && !!this.course.theme.colours[this.themeType];
        },
        themeType() {
            return this.type === 'finish' ? 'cover' : 'standard';
        },
        backgroundColor() {
            if (this.hasBackgroundColour) return this.localScreen.object.bg_colour;
            if (this.hasTheme) return this.course.theme.colours[this.themeType].background;
            if (this.hasDefaultBackground) return '#F6F7F8';
            return null;
        },
        headingFont() {
            return this.course
                && this.course.theme
                && this.course.theme.font;
        },
        titleColour() {
            if (!this.hasTheme) return;
            const colour = this.course.theme.colours[this.themeType].headings;
            if (!colour && this.themeType === 'cover') return readable(hexToRgb(this.backgroundColor)).contrast;
            return colour;
        },
        buttonColour() {
            if (!this.hasTheme) return;
            return this.course.theme.colours[this.themeType].primary;
        },
        buttonColourContrast() {
            if (!this.hasTheme) return;
            return readable(hexToRgb(this.buttonColour)).contrast;
        },
        patternColour() {
            if (!this.hasTheme) return;
            return this.course.theme.colours.cover.pattern;
        },
        textColour() {
            return readable(hexToRgb(this.backgroundColor)).contrast;
        },
        colourRgb() {
            const colour = this.textColour || COLOUR_DARK;
            const [r, g, b] = hexToRgb(colour);
            return `${r}, ${g}, ${b}`;  
        },
        colourContrastRgb() {
            const colour = this.textColour || COLOUR_DARK;
            const [r, g, b] = hexToRgb(readable(hexToRgb(colour)).contrast);
            return `${r}, ${g}, ${b}`;
        },
        placeholderColour() {
            if (!this.textColour) return;
            const [r, g, b] = hexToRgb(this.textColour);
            return `rgba(${r}, ${g}, ${b}, 0.5)`;
        },
        colourClass() {
            if (this.backgroundColor && this.textColour === COLOUR_LIGHT) return 'translucent';
            if (this.backgroundColor && this.textColour === COLOUR_DARK) return 'translucent-dark';
            if (this.hasBackgroundImage) return 'translucent-dark';
            return null;
        },
        _editable() {
            return this.editable && !this.preview && !this.isGeneratingScreen;
        },
        screenComponent() {
            return this._editable ? this.editorComponent : this.viewerComponent;
        },
        editorComponent() {
            if (!this.screen) return;
            return {
                iaAcronym: AccordionEditor,
                iaChecklist: ChecklistEditor,
                iaHotspot: HotspotEditor,
                slideshow: SlideshowEditor,
                document: DocumentEditor,
                embed: EmbedEditor,
                finish: CoverEditor,
                quiz: QuizEditor,
                caseStudy: FlashCardEditor,
                process: ProcessEditor,
                textAndImage: TextAndImageEditor,
                video: VideoEditor,
            }[this.type];
        },
        viewerComponent() {
            if (!this.screen) return;
            return {
                iaAcronym: Accordion,
                iaChecklist: Checklist,
                iaHotspot: Hotspot,
                slideshow: Slideshow,
                embed: Embed,
                document: Document,
                video: Video,
                process: Process,
                quiz: Quiz,
                finish: Cover,
                caseStudy: FlashCard,
                textAndImage: TextAndImage
            }[this.type];
        },
        hasScreenItems() {
            if (!(this.screen && this.screen.type)) return false;
            return ['iaAcronym', 'caseStudy', 'process', 'quiz', 'iaHotspot', 'slideshow'].includes(this.screen.type.name);
        },
        isCompletable() {
            return isCompletable(this.screen && this.screen.type && this.screen.type.completable)
                || this.hasScreenItems
                || (this.screen.type.name === 'video' && this.screen.object.images.length > 0 && this.screen.object.images[0].enforce_completion);
        },
        hasDefaultTitle() {
            return this.localScreen.title === 'Course title';
        }
    },
    watch: {
        screen: {
            handler(to) {                
                if (!this.editable || to.type.name !== this.localScreen.type.name || this.hasDefaultTitle) this.localScreen = copy(to);
            },
            deep: true
        },
        isVisible: {
            handler(to) {
                if (to) {
                    this.startTime = Date.now();
                    this.onTileViewed();
                    this.setBodyBackground();
                    if (this.$refs.screen && this.$refs.screen.onVisible) this.$refs.screen.onVisible();
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.el = this.$el;
        if (this.$refs.scroll) this.scrollEl = this.$refs.scroll.$refs.scrollElement;
        if (this.screen.generate && this.editable && this.screen.type.name !== 'quiz') {
            this.isGenerating = true;
        }
    },
    beforeUnmount() {
        if (this.screen && !this.simple) this.$emit('screen:end', this.screen.id, Date.now() - this.startTime);
    },
    methods: {
        url,
        insertGeneratedItems(items) {
            const key = getItemKeyByType(this.screen.type);
            const imageKey = getImageKeyByType(this.screen.type);
            if (!this.screen.object[key]) this.localScreen.object[key] = [];

            const hasImages = items.some(item => item.image);

            let index = 0;
            const interval = setInterval(() => {
                const item = items[index];
                item[imageKey] = item.image;
                if (typeof item === 'string') {
                    this.localScreen.object[key].push({ description: item });
                } else {
                    if (this.localScreen.object[key][index]) {
                        this.localScreen.object[key][index] = item;
                    } else {
                        this.localScreen.object[key].push(item);
                    }
                }
                if (this.$refs.screen && this.$refs.screen.select) {
                    const _index = index;
                    this.$nextTick(() => this.$refs.screen.select(_index));
                }
                index++;
                if (index === items.length) {
                    useBuilderState.updateScreen(this.localScreen);
                    clearInterval(interval);
                }
            }, hasImages ? 2000 : 1000);

            useBuilderState.clearGeneratedItems(this.screen.id);
        },
        onScreenUpdate(screen = null) {
            if (!this.editable) return;
            if (this.placeholder) return;
            const updated = { ...this.localScreen, ...screen, id: this.screen.id };
            const isUpdating = useBuilderState.updateScreen(updated);
            if (isUpdating) this.localScreen = updated;
        },
        onSave(saved = true) {
            if (!saved) return;
            this.$emit('saved');
        },
        onTileViewed() {
            if (this.editable || this.thumbnail || this.simple) return;
            if (!this.isCompletable) this.setTileComplete();
            this.$emit('screen:start', this.screen.id);
        },
        setTileComplete(event) {
            if (this.editable || this.thumbnail || this.simple) return;
            this.$emit('screen:complete', this.screen.id, event);
        },
        setBodyBackground() {
            if (this.editable || this.thumbnail || this.simple) return;
            document.body.style.backgroundColor = this.backgroundColor;
        },
        previous() {
            if (this.$refs.screen && this.$refs.screen.previous) {
                return this.$refs.screen.previous();
            } else {
                return false;
            }
        },
        next() {
            if (this.$refs.screen && this.$refs.screen.next) {
                return this.$refs.screen.next();
            } else {
                return false;
            }
        },
        suggestScreen(request = {}) {
            if (this.isGeneratingScreen) return;
            this.isGeneratingScreen = true;
            this.localScreen.generate = false;
            this.localScreen.generating = true;
            useBuilderState.updateScreen(this.localScreen);
            
            const { prompt, content } = request;
            return this.createGeneratedScreen(prompt, content)
                .catch(() => {
                    this.isGenerateError = true;
                });
        },
        async createGeneratedScreen(prompt, content = null) {
            let result;
            let data;
            const simpleType = {
                'iaChecklist': 'checklist',
                'caseStudy': 'flashcards',
                'process': 'process',
                'iaAcronym': 'list',
                'textAndImage': 'text'
            }[this.screen.type.name] || 'text';
            try {
                const response = await this.$http.post('/assistant/screen/generate', { prompt, content, type: simpleType });
                data = response.data;
                result = data.result;
            } catch {
                this.$track.event('AI Generation Failed', { 'ai_response': data });
                return Promise.reject();
            }

            this.localScreen.title = result.title;
            if (simpleType === 'text') {
                this.localScreen.description = result.description;
                const asset = await this.getSuggestedImage(result.title, result.description);
                this.localScreen.asset = asset;
            } else {
                const key = getItemKeyByType(this.screen.type.name);
                this.localScreen.object[key] = result.items;
                if (this.screen.type.name === 'process') {
                    await Promise.all(this.localScreen.object[key].map(async item => {
                        item[getImageKeyByType(this.screen.type.name)] = await this.getSuggestedImage(item.title, item.description);
                    }));
                }
            }

            this.localScreen.generating = false;
            useBuilderState.updateScreen(this.localScreen);
        },
        getSuggestedImage(title, description) {
            return this.$http.get(`/image/suggest`, { params: { title, description } })
                .then(({ data }) => data);
        },
        cancelGenerating() {
            if (!this.isGeneratingScreen) return;
            this.isGenerating = true;
            this.isGeneratingScreen = false;
            this.localScreen.generating = false;
            useBuilderState.updateScreen(this.localScreen);
        },
        confirmGenerating() {
            if (!this.isGeneratingScreen) return;
            this.isGeneratingScreen = false;
        },
    }
};
</script>

<style lang="less" scoped>
.screen {
    display: flex;
    flex-direction: column;
    perspective: 1000px; // Force stacking context
    background-color: #FFF;
    overflow: hidden;
    .photo-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style>

<style lang="less">
article.screen {
    --screen-padding: 24px;
    &.xl { --screen-padding: 96px; }
    &.lg { --screen-padding: 72px; }
    &.md { --screen-padding: 48px; }
    &.mobile {
        --screen-padding: 40px;
        .screen-inner {
            padding-bottom: 64px;
        }
        .screen-header {
            padding: 0 0 24px 0;
        }
        &:not(.finish) {
            .screen-inner {
                .screen-content {
                    .screen-component-wrapper {
                        justify-content: flex-start;
                    }
                    .screen-component {
                        margin: 0;
                    }
                }
            }  
        }
    }
    .background-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--coal);
    }
    .screen-observer {
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
    .screen-inner {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: var(--screen-padding);
        .screen-content {
            min-height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            overflow: hidden;
            .screen-component-wrapper {
                flex: 1 1 auto;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }
            .screen-component {
                max-width: 900px;
                width: 100%;
                margin: auto;
            }
        }
    }
    .screen-header {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 0 0 48px 0;
        .txt-component.heading {
            font-family: var(--title-font, var(--heading));
            color: var(--title-colour, inherit);
        }
    }
    .full-width {
        margin-left: calc(-1 * (var(--screen-padding) + var(--bleed, 0px)));
        margin-right: calc(-1 * (var(--screen-padding) + var(--bleed, 0px)));
    }
    .full-height {
        flex: 1 1 auto;
        margin-bottom: calc(-1 * var(--screen-padding));
    }
    .pad {
        padding: 0 var(--screen-padding) var(--screen-padding) var(--screen-padding);
    }
    &.placeholder {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        .screen-inner { opacity: 0.5; }
        .float-nav { display: none; }
    }
    .float-nav {
        display: flex;
        top: 0;
        left: 0;
        bottom: unset;
        margin: 40px 48px;
        .action-bar {
            opacity: 0;
            transition: var(--smooth-fast);
            border-radius: 8px;
        }
    }
    .steps-navigation, .compact-nav {
        max-width: none;
        background: none;
        border: none;
    }
    > .image-popup {
        top: 88px;
        left: 50%;
        transform: translateX(-50%);
    }
    &:hover, &.touch {
        .float-nav .action-bar { opacity: 1; }
    }
    .float-nav.screen-generate {
        .toolbar { 
            margin: auto;
            min-width: 616px;
            .animation { margin: 0; }
            hr {
                border: 0;
                border-right: 1px solid var(--smoke);
                height: 24px;
            }
            > .txt-component { flex: 1; }
        }
    }
}
</style>
